"use client";

export interface CarePlanGraphBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  layoutWidth: number;
  layoutHeight: number;
}

/** Measure the real styled graph, including negative coordinates and overflow. */
export function measureCarePlanGraph(graph: HTMLElement): CarePlanGraphBounds {
  const bounds = graph.getBoundingClientRect();
  let left = 0;
  let top = 0;
  let right = Math.max(bounds.width, graph.scrollWidth);
  let bottom = Math.max(bounds.height, graph.scrollHeight);
  graph.querySelectorAll("*").forEach((node) => {
    const rect = node.getBoundingClientRect();
    left = Math.min(left, rect.left - bounds.left);
    top = Math.min(top, rect.top - bounds.top);
    right = Math.max(right, rect.right - bounds.left);
    bottom = Math.max(bottom, rect.bottom - bounds.top);
  });
  return {
    left,
    top,
    right,
    bottom,
    width: Math.ceil(right - left),
    height: Math.ceil(bottom - top),
    layoutWidth: bounds.width || right,
    layoutHeight: bounds.height || bottom,
  };
}

/** Uniform fit for a content-free preview frame, independent of paper size. */
export function fitCarePlanGraph(
  bounds: Pick<CarePlanGraphBounds, "width" | "height">,
  viewport: { width: number; height: number },
): number {
  if (
    ![bounds.width, bounds.height].every(
      (value) => Number.isFinite(value) && value > 0,
    )
  ) {
    throw new Error("The care plan has no printable dimensions.");
  }
  return Math.min(
    1,
    viewport.width / bounds.width,
    viewport.height / bounds.height,
  );
}

/** Shared neutral compact graph styling for print and real-canvas previews. */
export const CARE_PLAN_PRINT_GRAPH_CSS = `
.care-print-graph, .care-print-graph *, .care-print-graph *::before, .care-print-graph *::after {
  background-color: white !important; background-image: none !important; color: #111 !important;
  border-color: #111 !important; box-shadow: none !important; text-shadow: none !important;
  animation: none !important; transition: none !important; transform: none !important;
  opacity: 1 !important; filter: none !important; backdrop-filter: none !important;
  text-overflow: clip !important; -webkit-line-clamp: unset !important; max-height: none !important;
  white-space: normal !important; overflow: visible !important; color-scheme: light !important;
}
.care-print-graph [data-step-card] { width: 200px !important; }
.care-print-graph [data-step-card] [data-step-card] { width: 100% !important; }
.care-print-graph [data-step-card] > div { padding: 8px !important; }
.care-print-graph .text-body { font-size: 12px !important; line-height: 16px !important; }
.care-print-graph .text-caption { font-size: 10px !important; line-height: 14px !important; }
/* Use available card/label space, not Badge's w-min word-sized columns. */
.care-print-graph [data-care-plan-summary-badge] { width: fit-content !important; max-width: 100% !important; min-width: 0 !important; }
.care-print-graph [data-care-plan-summary-badge] > :not([data-care-plan-summary-label]) { flex-shrink: 0 !important; }
.care-print-graph [data-care-plan-summary-label] { min-width: 0 !important; overflow-wrap: anywhere !important; }
.care-print-graph [data-care-plan-connector-label] { width: max-content !important; flex-shrink: 0 !important; white-space: nowrap !important; }
/* Branch labels may use the same bounded width as a step card, not an 80px slot. */
.care-print-graph [data-care-plan-branch-label] { width: fit-content !important; max-width: 200px !important; min-width: 0 !important; flex-shrink: 0 !important; }
.care-print-graph [data-care-plan-branch-heading], .care-print-graph [data-care-plan-tag-group], .care-print-graph [data-care-plan-tag] { width: fit-content !important; max-width: 100% !important; min-width: 0 !important; }
.care-print-graph [data-care-plan-branch-name], .care-print-graph [data-care-plan-tag-label] { min-width: 0 !important; overflow-wrap: anywhere !important; }
.care-print-graph [data-care-plan-tag-dot] { flex-shrink: 0 !important; }
/* Badge's w-min needs its no-wrap restored only for compact summary counts. */
.care-print-graph [data-care-plan-summary-count] { width: max-content !important; flex-shrink: 0 !important; }
.care-print-graph [data-care-plan-summary-count], .care-print-graph [data-care-plan-summary-count] * { white-space: nowrap !important; }
/* Match the title's 16px icon slot + 6px gap, without changing live cards. */
.care-print-graph [data-care-plan-card-date] { gap: 6px !important; }
.care-print-graph [data-care-plan-date-icon] { width: 16px !important; height: 16px !important; }
.care-print-graph [data-care-plan-date-icon] > svg { flex-shrink: 0 !important; }
.care-print-graph [class*="line-clamp-"] { display: block !important; }
.care-print-graph .truncate, .care-print-graph [class*="line-clamp-"] { min-width: 0 !important; overflow-wrap: anywhere !important; }
/* These backgrounds ARE connections, not card decoration. Keep their geometry. */
.care-print-graph .bg-border { background-color: #111 !important; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
.care-print-graph svg { background: transparent !important; }
.care-print-graph svg * { background: transparent !important; }
.care-print-graph svg [stroke]:not([stroke="none"]) { stroke: #111 !important; }
.care-print-graph svg [fill]:not([fill="none"]) { fill: #111 !important; }
`;

const PRINT_CSS = `
@page { size: auto; margin: 0.5in; }
html, body { display: block !important; width: auto !important; height: auto !important; min-height: 0 !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; background: white !important; color: #111 !important; color-scheme: light !important; }
.care-print-toolbar { padding: 16px; font: 14px sans-serif; }
.care-print-page { position: relative; box-sizing: border-box; width: min(80vw, 800px); height: min(70vh, 600px); min-width: 200px; min-height: 200px; resize: both; margin: 16px auto; background: white; overflow: hidden; break-inside: avoid; break-after: auto; }
.care-print-header { position: absolute; top: 0; left: 0; right: 0; box-sizing: border-box; height: 32px; font: 12px/16px sans-serif; overflow: visible; overflow-wrap: anywhere; }
.care-print-viewport { position: absolute; inset: 32px 0 0; overflow: hidden; }
.care-print-document-svg { display: block; width: 100%; height: 100%; }
.care-print-surface { position: relative; overflow: visible; }
.care-print-page .care-print-graph { position: absolute !important; margin: 0 !important; }
.care-print-measure { position: absolute; left: 0; top: 0; visibility: hidden; width: max-content; }
${CARE_PLAN_PRINT_GRAPH_CSS}
@media print {
  .care-print-toolbar { display: none !important; }
  /* Chromium's print viewport includes the physical page margins. */
  .care-print-page { width: 100%; height: calc(100vh - 1in - 1px); min-width: 0; min-height: 0; resize: none; margin: 0 !important; }
}
`;

function cloneGraph(
  canvas: HTMLElement,
  doc: Document,
  prefix: string,
): HTMLElement {
  const clone = doc.importNode(canvas, true);
  clone.classList.add("care-print-graph");
  clone.removeAttribute("aria-hidden");
  clone
    .querySelectorAll("script, iframe, object, embed")
    .forEach((node) => node.remove());
  const nodes = [
    clone,
    ...Array.from(clone.querySelectorAll<HTMLElement>("*")),
  ];
  const ids = new Map<string, string>();
  nodes.forEach((node, index) => {
    if (node.id) {
      ids.set(node.id, `${prefix}-${index}`);
      node.id = `${prefix}-${index}`;
    }
  });
  nodes.forEach((node) => {
    for (const attribute of Array.from(node.attributes)) {
      if (
        /^on/i.test(attribute.name) ||
        /^\s*javascript:/i.test(attribute.value)
      ) {
        node.removeAttribute(attribute.name);
        continue;
      }
      let value = attribute.value.replace(
        /url\(["']?#([^)'"\s]+)["']?\)/g,
        (match, id: string) => (ids.has(id) ? `url(#${ids.get(id)})` : match),
      );
      if (
        attribute.name !== "id" &&
        value.startsWith("#") &&
        ids.has(value.slice(1))
      ) {
        value = `#${ids.get(value.slice(1))}`;
      }
      if (
        ["for", "aria-labelledby", "aria-describedby"].includes(attribute.name)
      ) {
        value = value
          .split(" ")
          .map((id) => ids.get(id) ?? id)
          .join(" ");
      }
      node.setAttribute(attribute.name, value);
    }
    // Inline !important must not defeat the neutral print stylesheet.
    for (const property of Array.from(node.style ?? [])) {
      if (
        /^(background|color|border.*color|box-shadow|text-shadow|animation|transition|transform|opacity|filter|backdrop-filter|text-overflow|white-space|overflow|max-height|-webkit-line-clamp)/.test(
          property,
        )
      ) {
        node.style.removeProperty(property);
      }
    }
  });
  return clone;
}

async function withTimeout<T>(
  promise: Promise<T>,
  message: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), 15000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** Transport an already expanded, unzoomed provider graph into a safe print preview. */
export async function printCarePlanCanvas(
  canvas: HTMLElement,
  printWindow: Window,
  title: string,
): Promise<void> {
  if (printWindow.closed)
    throw new Error("The care plan print window was closed.");
  const doc = printWindow.document;
  doc.head.replaceChildren();
  doc.body.replaceChildren();
  doc.documentElement.removeAttribute("class");
  doc.documentElement.removeAttribute("style");
  doc.body.removeAttribute("class");
  doc.body.removeAttribute("style");
  doc.title = title;
  const sourceWindow = canvas.ownerDocument.defaultView;
  if (!sourceWindow)
    throw new Error("The care plan source document is unavailable.");
  // App print CSS sets html/body to 11px. Preserve the source rem scale before
  // measurement and in print media, rather than measuring at 16px then shrinking.
  for (const [target, source] of [
    [doc.documentElement, canvas.ownerDocument.documentElement],
    [doc.body, canvas.ownerDocument.body],
  ]) {
    const computed = sourceWindow.getComputedStyle(source);
    // Next's local-font body class supplies a generated font-face family. Its
    // computed value resolves font variables without carrying theme classes.
    for (const property of [
      "font-family",
      "font-size",
      "line-height",
      "font-weight",
      "font-style",
    ]) {
      target.style.setProperty(
        property,
        computed.getPropertyValue(property),
        "important",
      );
    }
  }
  // Font/image URLs in inline styles otherwise resolve against about:blank.
  const base = doc.createElement("base");
  base.href = canvas.ownerDocument.baseURI;
  doc.head.append(base);
  const toolbar = doc.createElement("div");
  toolbar.className = "care-print-toolbar";
  const status = doc.createElement("span");
  status.textContent = "Preparing care plan… ";
  const button = doc.createElement("button");
  button.textContent = "Print";
  button.type = "button";
  button.disabled = true;
  button.addEventListener("click", () => printWindow.print());
  toolbar.append(status, button);
  doc.body.append(toolbar);

  try {
    const pendingStyles: Promise<void>[] = [];
    canvas.ownerDocument
      .querySelectorAll("style, link[rel~='stylesheet']")
      .forEach((source) => {
        const copy = doc.importNode(source, true) as
          | HTMLStyleElement
          | HTMLLinkElement;
        if (source.tagName === "LINK") {
          const link = copy as HTMLLinkElement;
          link.href = (source as HTMLLinkElement).href;
          pendingStyles.push(
            new Promise((resolve, reject) => {
              link.onload = () => resolve();
              link.onerror = () =>
                reject(
                  new Error("A care plan stylesheet could not be loaded."),
                );
            }),
          );
        }
        doc.head.append(copy);
      });
    const style = doc.createElement("style");
    style.textContent = PRINT_CSS;
    doc.head.append(style);
    const measure = doc.createElement("div");
    measure.className = "care-print-measure";
    const graph = cloneGraph(canvas, doc, "measure");
    measure.append(graph);
    doc.body.append(measure);
    await withTimeout(
      Promise.all(pendingStyles),
      "Timed out loading care plan styles.",
    );
    // Trigger styled layout so the inherited source face (including graph weight
    // variants) is requested before observing FontFaceSet.ready.
    void graph.offsetWidth;
    if (doc.fonts)
      await withTimeout(doc.fonts.ready, "Timed out loading care plan fonts.");
    await withTimeout(
      Promise.all(
        Array.from(graph.querySelectorAll("img")).map(async (image) => {
          if (image.decode) await image.decode();
        }),
      ),
      "Timed out loading care plan images.",
    );
    if (printWindow.closed)
      throw new Error("The care plan print window was closed.");

    // globals.css also has generic print rules for .rounded-lg, .border-t,
    // spacing utilities, and message visibility. Freeze the neutral screen
    // layout so switching media cannot compress cards or hide graph nodes.
    // Read all computed declarations before writing, avoiding incremental reflow.
    const layoutProperties = [
      "display",
      "font-size",
      "line-height",
      "font-weight",
      "padding-top",
      "padding-right",
      "padding-bottom",
      "padding-left",
      "margin-top",
      "margin-right",
      "margin-bottom",
      "margin-left",
      "min-height",
      "height",
      "min-width",
      "max-width",
      "width",
    ];
    const layouts = [
      graph,
      ...Array.from(graph.querySelectorAll<HTMLElement>("*")),
    ].map((node) => {
      const computed = printWindow.getComputedStyle(node);
      return {
        node,
        values: layoutProperties.map(
          (property) =>
            [property, computed.getPropertyValue(property)] as const,
        ),
      };
    });
    layouts.forEach(({ node, values }) => {
      values.forEach(([property, value]) => {
        if (value) node.style.setProperty(property, value, "important");
      });
    });

    const bounds = measureCarePlanGraph(graph);
    // Validate the measured graph before building the responsive document.
    if (
      ![bounds.width, bounds.height].every(
        (value) => Number.isFinite(value) && value > 0,
      )
    ) {
      throw new Error("The care plan has no printable dimensions.");
    }
    const page = doc.createElement("section");
    page.className = "care-print-page";
    const header = doc.createElement("div");
    header.className = "care-print-header";
    header.textContent = title;
    const viewport = doc.createElement("div");
    viewport.className = "care-print-viewport";
    // SVG owns fitting to the native page box; graph direction never chooses
    // physical paper size or orientation. Keep the frozen HTML layout intact.
    const svgNamespace = "http://www.w3.org/2000/svg";
    const svg = doc.createElementNS(svgNamespace, "svg");
    svg.classList.add("care-print-document-svg");
    svg.setAttribute("viewBox", `0 0 ${bounds.width} ${bounds.height}`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "100%");
    const foreignObject = doc.createElementNS(svgNamespace, "foreignObject");
    foreignObject.setAttribute("x", "0");
    foreignObject.setAttribute("y", "0");
    foreignObject.setAttribute("width", `${bounds.width}`);
    foreignObject.setAttribute("height", `${bounds.height}`);
    const surface = doc.createElementNS("http://www.w3.org/1999/xhtml", "div");
    surface.className = "care-print-surface";
    surface.style.width = `${bounds.width}px`;
    surface.style.height = `${bounds.height}px`;
    graph.style.setProperty("width", `${bounds.layoutWidth}px`, "important");
    graph.style.setProperty("height", `${bounds.layoutHeight}px`, "important");
    graph.style.boxSizing = "border-box";
    graph.style.left = `${-bounds.left}px`;
    graph.style.top = `${-bounds.top}px`;
    surface.append(graph);
    foreignObject.append(surface);
    svg.append(foreignObject);
    viewport.append(svg);
    page.append(header, viewport);
    doc.body.append(page);
    measure.remove();
    status.textContent =
      "Choose paper size and orientation in the print dialog. ";
    button.disabled = false;
  } catch (error) {
    doc.querySelector(".care-print-measure")?.remove();
    status.textContent =
      "Unable to prepare the care plan. Close this window and try again. ";
    throw error;
  }
}
